import { fetchStream, proxyUrl, type MediaRef } from "./api.ts";
import { appendChildren, el, logoImage, showToast } from "./dom.ts";
import { icons } from "./icons.ts";
import { getState, isFavorite, toPlaybackRecord, toggleFavorite } from "./store.ts";
import type { ProviderSummary, VideoSummary } from "./api.ts";

export interface PosterOptions {
  /** Position in a ranked row (renders the numeral over the art). */
  rank?: number;
  providerLabel?: string;
  /** Explicit overlay tag; the provider name is the default. */
  tag?: string;
  onOpen?: (video: VideoSummary) => void;
  progress?: number;
  /** Override the detail route the card points at. */
  href?: string;
}

/**
 * Thumbnails are served through the worker's signed proxy, which hands out
 * worker-relative paths (`/api/proxy?...`). The SPA is hosted on a different
 * origin than the API, so those paths have to be resolved against the API
 * origin before the browser can load them.
 */
let apiOriginPromise: Promise<string> | null = null;

function apiOrigin(): Promise<string> {
  apiOriginPromise ??= fetch("/config.json")
    .then((response) => (response.ok ? response.json() : null))
    .then((config: { apiBase?: string } | null) => {
      const base = (config?.apiBase ?? "").replace(/\/$/, "");
      return base.endsWith("/api") ? base.slice(0, -4) : "";
    })
    .catch(() => "");
  return apiOriginPromise;
}

export async function resolveImageUrl(source: string): Promise<string> {
  if (!source.startsWith("/")) return source;
  const origin = await apiOrigin();
  return origin ? `${origin}${source}` : source;
}

interface ImageLoader {
  /** Offer another source; ignored once an image has loaded. */
  add: (source: string) => void;
}

/** Titled placeholder shown when a thumbnail is missing or fails to load. */
function fallbackNode(title: string): HTMLElement {
  return el("span", { class: "poster-fallback" }, el("span", { class: "poster-fallback-text", text: title }));
}

/**
 * Fill an artwork frame with the first source that loads. While the image is
 * in flight the frame keeps its bone placeholder; when every source fails a
 * titled fallback takes over, so a card never renders as a broken image.
 */
function attachImage(
  art: HTMLElement,
  alt: string,
  sources: Promise<string[]>,
  size: { width: number; height: number } = { width: 400, height: 600 },
): ImageLoader {
  const queue: string[] = [];
  let pending = false;
  let settled = false;
  let closed = false;

  const attempt = (): void => {
    if (settled) return;
    const src = queue.shift();
    if (!src) {
      if (closed && !art.querySelector("img") && !art.querySelector(".poster-fallback")) {
        art.prepend(fallbackNode(alt));
      }
      return;
    }
    pending = true;
    const image = el("img", {
      class: "poster-img",
      attrs: {
        src,
        alt,
        width: String(size.width),
        height: String(size.height),
        loading: "lazy",
        decoding: "async",
        referrerpolicy: "no-referrer",
      },
    });
    image.addEventListener("load", () => {
      pending = false;
      settled = true;
      image.dataset.loaded = "true";
      art.querySelector(".poster-fallback")?.remove();
      // Providers deliver both stills and poster art. The frame follows the
      // picture: one listing (or rail) takes the shape of its first cover, so a
      // row never mixes a 16:9 still with a 2:3 poster.
      const ratio = image.naturalHeight > image.naturalWidth * 1.02 ? "portrait" : "wide";
      art.dataset.ratio = ratio;
      const scope = art.closest(".grid, .rail-track, .spotlight-grid, .provider-track");
      if (scope instanceof HTMLElement && !scope.dataset.ratio) scope.dataset.ratio = ratio;
    });
    image.addEventListener("error", () => {
      pending = false;
      settled = false;
      image.remove();
      attempt();
    });
    art.prepend(image);
    // A cached image can already be complete before the listener attached.
    if (image.complete && image.naturalWidth > 0) {
      pending = false;
      settled = true;
      image.dataset.loaded = "true";
      art.querySelector(".poster-fallback")?.remove();
    }
  };

  void sources.then((resolved) => {
    queue.push(...resolved);
    closed = true;
    attempt();
  });

  return {
    add: (source: string) => {
      if (settled) return;
      queue.push(source);
      if (!pending) attempt();
    },
  };
}

export function displayTitle(raw: string): string {
  const trimmed = raw.replace(/^\s*\[[^\]]*\]\s*[-–—:]?\s*/, "").replace(/\s{2,}/g, " ").trim();
  return trimmed.length > 0 ? trimmed : raw.trim();
}

export function posterCard(video: VideoSummary, options: PosterOptions = {}): HTMLElement {
  const title = displayTitle(video.title);
  const provider = options.providerLabel ?? video.provider;
  const cell = el("li", { class: "poster-cell" });
  const art = el("span", { class: "poster-art" });
  // A later page can carry a blank image field; a card with an empty src is a
  // broken tile, so blanks are dropped before anything is requested.
  const sources = [video.image, video.thumbnail].filter(
    (value): value is string => typeof value === "string" && value.trim() !== "",
  );
  if (sources.length > 0) {
    attachImage(art, "", Promise.all(sources.map(resolveImageUrl)), { width: 660, height: 372 });
  }
  // No artwork is not an error state on a film tile: the title already sits on
  // the frame, so a second copy of it as fallback text only repeats itself.

  // The provider chip is the one accent fill on a card.
  const tag = options.tag ?? provider;
  if (tag) {
    art.append(el("span", { class: "poster-brand", text: tag }));
  }
  if (options.rank) {
    art.append(
      el("span", { class: "poster-rank", text: String(options.rank).padStart(2, "0") }),
    );
  }

  // The play affordance is drawn, not a nested control: the whole card is the
  // link, so it appears on hover and on keyboard focus.
  art.append(el("span", { class: "poster-play", attrs: { "aria-hidden": "true" }, html: icons.play }));

  // The frame carries the picture and its one tag. The caption under it is the
  // title and a single metadata line, the way a film catalogue prints a poster.
  const caption = el(
    "span",
    { class: "poster-caption" },
    el("span", { class: "poster-title", text: title }),
    el(
      "span",
      { class: "poster-sub" },
      el("span", { class: "poster-sub-provider", text: provider }),
      video.duration ? el("span", { class: "poster-sub-sep", attrs: { "aria-hidden": "true" }, text: "·" }) : null,
      video.duration ? el("span", { class: "poster-sub-duration", text: video.duration }) : null,
    ),
  );

  const href =
    options.href ??
    `#/watch/${encodeURIComponent(video.provider)}/${encodeURIComponent(video.id)}`;
  const card = el("a", {
    class: "poster",
    attrs: { href, "aria-label": video.title },
  });
  card.append(art, caption);

  if (options.progress && options.progress > 0) {
    const bar = el("span", { class: "poster-progress" }, el("span", { class: "poster-progress-fill" }));
    const fill = bar.firstElementChild as HTMLElement;
    fill.style.transform = `scaleX(${Math.min(1, options.progress)})`;
    art.append(bar);
    // a resumable card says so in words, not only in the accent seam
    art.append(el("span", { class: "poster-resume", text: "Resume" }));
  }

  // The chip and the duration already ride the artwork. A second line under the
  // frame repeated both, so the card is the frame and nothing else.

  card.addEventListener("click", (event) => {
    if (!options.onOpen) return;
    event.preventDefault();
    options.onOpen(video);
  });
  attachHoverPreview(card, video);
  cell.append(card);

  if (getState().settings.blurArtwork) {
    const reveal = el("button", {
      class: "poster-reveal",
      attrs: {
        type: "button",
        "aria-pressed": "false",
        "aria-label": `Show artwork for ${title}`,
      },
      text: "Show",
    });
    reveal.addEventListener("click", () => {
      const shown = cell.dataset.revealed !== "true";
      cell.dataset.revealed = String(shown);
      reveal.setAttribute("aria-pressed", String(shown));
      reveal.textContent = shown ? "Hide" : "Show";
    });
    cell.append(reveal);
  }

  return cell;
}

let preview: { video: HTMLVideoElement; card: HTMLElement } | null = null;

/** A stream playing under the pointer must not survive a route change. */
export function dismissHoverCard(): void {
  stopPreview();
}

function stopPreview(): void {
  if (!preview) return;
  const { video, card } = preview;
  preview = null;
  video.pause();
  video.removeAttribute("src");
  video.load();
  video.remove();
  card.removeAttribute("data-previewing");
}

/**
 * Hovering a card plays it. A still only says what a title looks like; the
 * first seconds of the stream answer whether it is worth opening. One preview
 * at a time, muted, and never under a reader who asked for less motion.
 */
function attachHoverPreview(card: HTMLElement, video: VideoSummary): void {
  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  let timer = 0;
  let token = 0;

  const start = async () => {
    const mine = ++token;
    const art = card.querySelector(".poster-art");
    if (!art) return;
    try {
      const media = await fetchStream(video.provider, video.id);
      if (mine !== token) return;
      const ref =
        media.find((entry: MediaRef) => entry.type === "mp4" && (entry.playUrl || entry.url)) ?? media[0];
      if (!ref) return;

      stopPreview();
      const element = el("video", {
        class: "poster-preview",
        attrs: {
          muted: "",
          loop: "",
          playsinline: "",
          preload: "metadata",
          "aria-hidden": "true",
          tabindex: "-1",
        },
      }) as HTMLVideoElement;
      element.muted = true;
      element.loop = true;
      element.playsInline = true;
      element.src = proxyUrl(ref);
      art.append(element);
      preview = { video: element, card };

      // The still stays until a frame is actually moving. A stream that 404s,
      // 502s or never buffers leaves the card exactly as it was.
      const discard = () => {
        if (preview?.video === element) stopPreview();
        else element.remove();
      };
      const guard = window.setTimeout(() => {
        if (element.readyState < 2) discard();
      }, 4500);
      element.addEventListener(
        "playing",
        () => {
          window.clearTimeout(guard);
          if (preview?.video === element) card.dataset.previewing = "true";
        },
        { once: true },
      );
      element.addEventListener("error", discard, { once: true });
      await element.play().catch(discard);
    } catch {
      /* no stream, no preview */
    }
  };

  const stop = () => {
    window.clearTimeout(timer);
    token += 1;
    if (preview?.card === card) stopPreview();
  };

  card.addEventListener("mouseenter", () => {
    timer = window.setTimeout(() => void start(), 420);
  });
  card.addEventListener("mouseleave", stop);
  card.addEventListener("click", stop);
  card.addEventListener("focus", stop);
}

export interface RailOptions {
  title: string;
  meta?: string;
  items: VideoSummary[];
  providerLabel?: string;
  /** Provider display names by id, for rows that mix providers. */
  providerNames?: Record<string, string>;
  /** Render the row as a numbered chart. */
  ranked?: boolean;
  /** Progress per card, keyed by video id (0–1). */
  progress?: Record<string, number>;
  /** Row action, shown as "View all". */
  viewAll?: { label?: string; onOpen: () => void };
  onOpen: (video: VideoSummary) => void;
}

/** A section head with a horizontal row of tiles under it. */
export function rail(options: RailOptions): HTMLElement {
  const track = el("ul", { class: "rail-track" });
  // The home mounts fourteen rows; painting their cards at once starts an image
  // request per card before the reader has scrolled anywhere. A row fills the
  // first time it comes near the viewport, and never again.
  const paint = (): void => {
    options.items.forEach((item, index) => {
      const card = posterCard(item, {
        providerLabel: options.providerLabel ?? options.providerNames?.[item.provider],
        rank: options.ranked ? index + 1 : undefined,
        progress: options.progress?.[item.id],
        onOpen: options.onOpen,
      });
      // Entrance stagger is capped at six cards; scrolling never replays it.
      card.style.setProperty("--row-index", String(Math.min(index, 5)));
      track.append(card);
    });
  };

  const scrollBy = (direction: number) => {
    track.scrollBy({ left: direction * (track.clientWidth * 0.82), behavior: "smooth" });
  };

  const prev = el("button", {
    class: "rail-arrow rail-arrow--prev",
    attrs: { "aria-label": "Scroll left", type: "button" },
    html: icons.chevron,
  });
  const next = el("button", {
    class: "rail-arrow",
    attrs: { "aria-label": "Scroll right", type: "button" },
    html: icons.chevron,
  });
  prev.addEventListener("click", () => scrollBy(-1));
  next.addEventListener("click", () => scrollBy(1));

  // A rail that has reached its end should say so; a dead arrow is worse than
  // no arrow. Arrow keys scroll the rail while any card in it has focus.
  const syncArrows = (): void => {
    const max = track.scrollWidth - track.clientWidth;
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= max - 1;
  };
  track.addEventListener("scroll", syncArrows, { passive: true });
  track.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    scrollBy(event.key === "ArrowLeft" ? -1 : 1);
  });
  const resizeObserver = new ResizeObserver(syncArrows);
  resizeObserver.observe(track);

  if ("IntersectionObserver" in window) {
    const watcher = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        watcher.disconnect();
        paint();
        syncArrows();
      },
      { rootMargin: "600px 0px" },
    );
    watcher.observe(track);
  } else {
    paint();
  }
  requestAnimationFrame(syncArrows);

  const viewAll = options.viewAll
    ? el("button", { class: "ghost-link", attrs: { type: "button" }, text: options.viewAll.label ?? "View all" })
    : null;
  viewAll?.addEventListener("click", () => options.viewAll?.onOpen());

  const head = el(
    "div",
    { class: "section-head" },
    el("h2", { class: "section-title", text: options.title }),
    options.meta ? el("span", { class: "section-meta", text: options.meta }) : null,
    el("div", { class: "section-actions" }, viewAll, prev, next),
  );

  return el("div", { class: "rail" }, head, track);
}

/**
 * Provider row: the catalogue's own logos and names as a horizontal index, so
 * the sites are one gesture from the hero.
 */
export function providerRail(
  providers: ProviderSummary[],
  options: { title: string; meta?: string; onOpen: (id: string) => void },
): HTMLElement {
  const track = el("ul", { class: "rail-track provider-track" });
  for (const provider of providers) {
    const tile = el("a", {
      class: "provider-tile",
      attrs: { href: `#/provider/${provider.id}`, "aria-label": provider.name },
    });
    tile.append(
      el("span", { class: "provider-mark" }, logoImage(provider.logo, provider.name)),
      el("span", { class: "provider-name", text: provider.name }),
    );
    tile.addEventListener("click", (event) => {
      event.preventDefault();
      options.onOpen(provider.id);
    });
    track.append(el("li", { class: "provider-cell" }, tile));
  }

  const scrollBy = (direction: number) => {
    track.scrollBy({ left: direction * (track.clientWidth * 0.82), behavior: "smooth" });
  };
  const prev = el("button", {
    class: "rail-arrow rail-arrow--prev",
    attrs: { "aria-label": "Scroll left", type: "button" },
    html: icons.chevron,
  });
  const next = el("button", {
    class: "rail-arrow",
    attrs: { "aria-label": "Scroll right", type: "button" },
    html: icons.chevron,
  });
  prev.addEventListener("click", () => scrollBy(-1));
  next.addEventListener("click", () => scrollBy(1));

  // A rail that has reached its end should say so; a dead arrow is worse than
  // no arrow. Arrow keys scroll the rail while any card in it has focus.
  const syncArrows = (): void => {
    const max = track.scrollWidth - track.clientWidth;
    prev.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= max - 1;
  };
  track.addEventListener("scroll", syncArrows, { passive: true });
  track.addEventListener("keydown", (event) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    scrollBy(event.key === "ArrowLeft" ? -1 : 1);
  });
  const resizeObserver = new ResizeObserver(syncArrows);
  resizeObserver.observe(track);

  // The row drifts on its own so the whole catalogue passes by without a
  // gesture. The moment anyone points at it, tabs into it or scrolls it, the
  // drift stops and the reader is in charge; it resumes when they leave.
  const calm = window.matchMedia("(prefers-reduced-motion: reduce)");
  let drift = 0;
  let paused = false;
  let frame = 0;
  let visible = true;
  const SPEED = 0.42;

  // scrollLeft rounds to whole pixels, so the position is carried in a float and
  // written back; adding 0.42 to the element every frame would read back as 0.
  let position = 0;
  const step = (): void => {
    frame = 0;
    if (!paused && visible && !calm.matches) {
      const max = track.scrollWidth - track.clientWidth;
      if (max > 8) {
        if (drift === 0) drift = SPEED;
        if (Math.abs(position - track.scrollLeft) > 2) position = track.scrollLeft;
        position += drift;
        if (position >= max) {
          position = max;
          drift = -SPEED;
        } else if (position <= 0) {
          position = 0;
          drift = SPEED;
        }
        track.scrollLeft = position;
      }
    }
    schedule();
  };
  const schedule = (): void => {
    if (frame || paused || !visible || calm.matches) return;
    frame = requestAnimationFrame(step);
  };
  const stop = (): void => {
    paused = true;
    drift = 0;
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
  };
  const resume = (): void => {
    paused = false;
    schedule();
  };

  track.addEventListener("pointerenter", stop);
  track.addEventListener("pointerleave", resume);
  track.addEventListener("focusin", stop);
  track.addEventListener("focusout", resume);
  track.addEventListener("wheel", stop, { passive: true });
  track.addEventListener("touchstart", stop, { passive: true });
  calm.addEventListener?.("change", () => (calm.matches ? stop() : resume()));

  (track as unknown as { __drift?: unknown }).__drift = () => ({ paused, visible, frame, drift, position, max: track.scrollWidth - track.clientWidth });
  if ("IntersectionObserver" in window) {
    new IntersectionObserver(
      (entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        if (!visible) {
          if (frame) cancelAnimationFrame(frame);
          frame = 0;
        } else {
          schedule();
        }
      },
      { threshold: 0.05 },
    ).observe(track);
  } else {
    schedule();
  }

  requestAnimationFrame(syncArrows);

  return el(
    "div",
    { class: "rail" },
    el(
      "div",
      { class: "section-head" },
      el("h2", { class: "section-title", text: options.title }),
      options.meta ? el("span", { class: "section-meta", text: options.meta }) : null,
      el("div", { class: "section-actions" }, prev, next),
    ),
    track,
  );
}

function skeletonCard(): HTMLElement {
  return el(
    "li",
    { class: "poster-cell" },
    el(
      "div",
      { class: "poster poster--skeleton", attrs: { "aria-hidden": "true" } },
      el(
        "span",
        { class: "poster-art skeleton" },
        el(
          "span",
          { class: "poster-body" },
          el("span", { class: "skeleton skeleton--line", attrs: { style: "width:86%" } }),
        ),
      ),
      el(
        "span",
        { class: "poster-meta" },
        el("span", { class: "skeleton skeleton--line skeleton--short", attrs: { style: "width:48%" } }),
      ),
    ),
  );
}

export function skeletonGrid(count = 12, label = "Loading titles"): HTMLElement {
  const grid = el("ul", { class: "grid", attrs: { "aria-label": label, "aria-busy": "true" } });
  for (let index = 0; index < count; index += 1) grid.append(skeletonCard());
  return grid;
}

export function skeletonRail(count = 6): HTMLElement {
  const track = el("ul", { class: "rail-track" });
  for (let index = 0; index < count; index += 1) track.append(skeletonCard());
  return el(
    "div",
    { class: "rail", attrs: { "aria-busy": "true" } },
    el(
      "div",
      { class: "section-head" },
      el("span", { class: "skeleton skeleton--line", attrs: { style: "width:220px" } }),
    ),
    track,
  );
}

export interface EmptyOptions {
  title: string;
  body?: string;
  action?: { label: string; onOpen: () => void };
}

/** One empty-state pattern: a line of state, and a real next action. */
export function emptyState(options: EmptyOptions): HTMLElement {
  const box = el(
    "div",
    { class: "empty" },
    el("p", { class: "empty-title", text: options.title }),
    options.body ? el("p", { class: "empty-body", text: options.body }) : null,
  );
  if (options.action) {
    const button = el("button", { class: "btn btn-secondary btn-sm", attrs: { type: "button" }, text: options.action.label });
    button.addEventListener("click", () => options.action?.onOpen());
    box.append(button);
  }
  return box;
}

export function errorState(message: string, retry?: () => void): HTMLElement {
  const box = el(
    "div",
    { class: "error", attrs: { role: "alert" } },
    el("p", { class: "error-message", text: message }),
  );
  if (retry) {
    const button = el("button", { class: "btn btn-secondary btn-sm", attrs: { type: "button" }, text: "Try again" });
    button.addEventListener("click", () => {
      box.remove();
      retry();
    });
    box.append(button);
  }
  const dismiss = el("button", {
    class: "coin coin--sm error-dismiss",
    attrs: { type: "button", "aria-label": "Dismiss error" },
    html: icons.close,
  });
  dismiss.addEventListener("click", () => box.remove());
  box.append(dismiss);
  return box;
}

export interface IndexRowOptions {
  title: string;
  state?: string;
  count?: string;
  leading?: HTMLElement;
  /** Real destination; rows are links so they work in a new tab. */
  href?: string;
  onOpen?: () => void;
}

/** A quiet index row: name, one line of state, an optional count. */
export function indexRow(options: IndexRowOptions): HTMLElement {
  const row = options.href
    ? el("a", { class: "index-row", attrs: { href: options.href } })
    : el("div", { class: "index-row" });
  appendChildren(
    row,
    options.leading ?? el("span", { class: "index-mark", attrs: { "aria-hidden": "true" } }),
    el(
      "span",
      { class: "index-main" },
      el("span", { class: "index-name", text: options.title }),
      options.state ? el("span", { class: "index-state", text: options.state }) : null,
    ),
    options.count ? el("span", { class: "index-count", text: options.count }) : null,
  );
  if (options.onOpen) {
    row.addEventListener("click", (event) => {
      if (options.href) event.preventDefault();
      options.onOpen?.();
    });
  }
  return el("li", { class: "index-item" }, row);
}

export function favoriteButton(video: VideoSummary, onChange?: () => void): HTMLElement {
  const button = el("button", {
    class: "btn btn-secondary",
    attrs: { type: "button", "aria-pressed": "false" },
    html: `${icons.heart}<span>Favourite</span>`,
  });
  const paint = () => {
    button.setAttribute("aria-pressed", String(isFavorite(video.provider, video.id)));
  };
  paint();
  button.addEventListener("click", (event) => {
    event.stopPropagation();
    const added = toggleFavorite(toPlaybackRecord(video));
    paint();
    showToast(added ? "Added to favourites" : "Removed from favourites");
    onChange?.();
  });
  return button;
}
